/**
 * @module Sagas/User
 * @desc User
 */

import { put, call } from 'redux-saga/effects';

import {
  loginSuccess,
  loginFailure,
  logoutSuccess,
  logoutFailure,
  recoverPasswordSuccess,
  recoverPasswordFailure,
  resetPasswordSuccess,
  resetPasswordFailure,
  updateProfileSuccess,
  updateProfileFailure,
  updatePasswordSuccess,
  updatePasswordFailure,
  showAlert,
} from 'actions/index';

import { push } from '../modules/history';

import reduxSagaFirebase from '../services/firebase';

export function* login(api, action) {
  const {
    payload: { email, password },
  } = action;
  try {
    const { user } = yield call(reduxSagaFirebase.auth.signInWithEmailAndPassword, email, password);

    // getIdToken returns the raw JWT; decode the payload to read custom claims (role)
    // Using getIdToken avoids Firebase SDK's internal Object.defineProperty usage in IdTokenResult
    const token = yield call(() => user.getIdToken(true));
    let role = null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      role = payload?.role ?? null;
    } catch (_) {
      role = null;
    }

    const profile = {
      displayName: user.displayName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      photoURL: user.photoURL,
      role,
    };

    yield call(api.setAuthorization, token);
    yield put(loginSuccess({ profile, token }));

    // Role-based redirect after login
    if (role === 'Business') {
      yield put(push('/dashboard/business'));
    } else {
      yield put(push('/dashboard/users'));
    }
  } catch (error) {
    yield put(loginFailure());
    yield put(showAlert(error?.message ?? 'Login failed', { variant: 'danger' }));
  }
}

export function* logout(api) {
  try {
    yield call(reduxSagaFirebase.auth.signOut);

    const token = null;

    yield call(api.setAuthorization, token);
    yield put(logoutSuccess());
    yield put(showAlert('Logout successful', { variant: 'success' }));
  } catch (error) {
    yield put(logoutFailure());
    yield put(showAlert(error?.message ?? 'Logout failed', { variant: 'danger' }));
  }
}

export function* recoverPassword(api, action) {
  const {
    payload: { email },
  } = action;
  try {
    const { status, data } = yield call(api.recoverPassword, email);
    if (status === 200) {
      yield put(recoverPasswordSuccess());
      yield put(showAlert('Password reset email sent. Check your inbox.', { variant: 'success' }));
    } else {
      yield put(recoverPasswordFailure());
      const msg = data?.error?.message ?? 'Something went wrong';
      yield put(showAlert(msg, { variant: 'danger' }));
    }
  } catch (error) {
    yield put(recoverPasswordFailure());
    yield put(showAlert(error?.message ?? 'Something went wrong', { variant: 'danger' }));
  }
}

export function* resetPassword(action) {
  const {
    payload: { token, password },
  } = action;
  try {
    yield call(reduxSagaFirebase.auth.confirmPasswordReset, token, password);
    yield put(resetPasswordSuccess());
    yield put(showAlert('Your password has been reset', { variant: 'success' }));
    push('/login');
  } catch (error) {
    yield put(resetPasswordFailure());
    yield put(showAlert(error?.message ?? 'Password reset failed', { variant: 'danger' }));
  }
}

export function* updateProfile(action) {
  const { payload } = action;
  const rsf = reduxSagaFirebase.auth;

  try {
    yield call(rsf.updateProfile, payload);
    yield put(showAlert('Your profile has been reset', { variant: 'success' }));
    yield put(updateProfileSuccess(payload));
  } catch (error) {
    yield put(updateProfileFailure());
    yield put(showAlert(error?.message ?? 'Profile update failed', { variant: 'danger' }));
  }
}

export function* updatePassword(action) {
  const {
    payload: { password },
  } = action;
  const rsf = reduxSagaFirebase.auth;

  try {
    yield call(rsf.updatePassword, password);
    yield put(showAlert('Your password has been reset', { variant: 'success' }));
    yield put(updatePasswordSuccess());
  } catch (error) {
    yield put(updatePasswordFailure());
    yield put(showAlert(error?.message ?? 'Password update failed', { variant: 'danger' }));
  }
}
